import { useEffect, useMemo, useState } from 'react'
import { estimateTokens, loadTokenizer, type TokenCounter } from '../lib/tokenizer'

/** Shared create/edit form for community characters. Owns field state and
 *  client-side validation; the parent handles submission and server errors. */

const MAX_TOKENS = 500
const MAX_CHARS = 10_000 // generous typing ceiling; the real cap is the token count
const MAX_LINKS = 3
export const MAX_TITLE = 100
export const MAX_TAGLINE = 300
export const MAX_CREATOR = 60
export const MAX_DESCRIPTION = 100_000
export const MAX_URL = 500

const inputClass =
  'w-full rounded-lg border border-edge bg-void-2 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition focus:border-signal/60'
const labelClass = 'mb-1.5 block text-xs uppercase tracking-[0.2em] text-slate-400'

function isValidHttpUrl(v: string): boolean {
  if (!v || /\s/.test(v)) return false
  try {
    const u = new URL(v)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

export type CharacterFormValues = {
  title: string
  tagline: string
  imageUrl: string
  links: string[]
  description: string
  lorebook: string
  creator: string
  /** Quiet field: left at its default by the normal UI flow. Sent as-is. */
  terms: string
}

export const emptyFormValues: CharacterFormValues = {
  title: '',
  tagline: '',
  imageUrl: '',
  links: [''],
  description: '',
  lorebook: '',
  creator: '',
  terms: 'no',
}

export function formValuesFrom(c: {
  title: string
  tagline: string
  imageUrl: string | null
  links: string[]
  description: string | null
  lorebook: string | null
  creator: string | null
}): CharacterFormValues {
  const links = [...c.links]
  while (links.length < 1) links.push('')
  return {
    title: c.title,
    tagline: c.tagline,
    imageUrl: c.imageUrl ?? '',
    links,
    description: c.description ?? '',
    lorebook: c.lorebook ?? '',
    creator: c.creator ?? '',
    terms: 'no',
  }
}

export function CharacterForm({
  initial,
  submitting,
  serverError,
  submitLabel,
  onSubmit,
}: {
  initial: CharacterFormValues
  submitting: boolean
  serverError: string | null
  submitLabel: string
  onSubmit: (values: CharacterFormValues) => void
}) {
  const [title, setTitle] = useState(initial.title)
  const [tagline, setTagline] = useState(initial.tagline)
  const [imageUrl, setImageUrl] = useState(initial.imageUrl)
  const [links, setLinks] = useState<string[]>(initial.links)
  const [creator, setCreator] = useState(initial.creator)
  const [description, setDescription] = useState(initial.description)
  const [lorebook, setLorebook] = useState(initial.lorebook)
  const [localError, setLocalError] = useState<string | null>(null)

  // exact counter swaps in once the tokenizer chunk loads; estimate until then
  const [countTokens, setCountTokens] = useState<TokenCounter | null>(null)
  useEffect(() => {
    let alive = true
    loadTokenizer()
      .then((fn) => {
        if (alive) setCountTokens(() => fn)
      })
      .catch(() => {
        /* estimate keeps working if the chunk fails to load */
      })
    return () => {
      alive = false
    }
  }, [])

  const tokens = useMemo(() => (countTokens ?? estimateTokens)(lorebook), [lorebook, countTokens])
  const tokenTone =
    tokens > MAX_TOKENS ? 'text-sos' : tokens > MAX_TOKENS * 0.9 ? 'text-amber-400' : 'text-slate-500'

  const error = localError ?? serverError

  const setLink = (i: number, value: string) =>
    setLinks((prev) => prev.map((l, idx) => (idx === i ? value : l)))

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (submitting) return
    setLocalError(null)

    // quiet field rides along uncontrolled, straight from the DOM
    const fd = new FormData(e.currentTarget)
    const terms = fd.get('terms')

    if (!title.trim()) return setLocalError('Your character needs a title.')
    if (!tagline.trim()) return setLocalError('Your character needs a tagline.')
    if (imageUrl.trim() && !isValidHttpUrl(imageUrl.trim()))
      return setLocalError('The image link must be a valid http(s) URL.')

    const cleanLinks = links.map((l) => l.trim()).filter(Boolean)
    if (cleanLinks.length > MAX_LINKS) return setLocalError(`At most ${MAX_LINKS} links are allowed.`)
    for (const l of cleanLinks) {
      if (!isValidHttpUrl(l)) return setLocalError('Every link must be a valid http(s) URL.')
    }
    if (lorebook.length > MAX_CHARS) return setLocalError('The lorebook entry is over the token cap.')
    if ((countTokens ?? estimateTokens)(lorebook) > MAX_TOKENS)
      return setLocalError('The lorebook entry is over the token cap.')

    onSubmit({
      title: title.trim(),
      tagline: tagline.trim(),
      imageUrl: imageUrl.trim(),
      links: cleanLinks,
      description: description.trim(),
      lorebook: lorebook.trim(),
      creator: creator.trim(),
      terms: typeof terms === 'string' ? terms : 'no',
    })
  }

  return (
    <form onSubmit={submit} className="space-y-6" noValidate>
      {error && (
        <div className="rounded-lg border border-sos/40 bg-sos/10 px-4 py-3 text-sm text-sos">
          {error}
        </div>
      )}

      <div>
        <label className={labelClass} htmlFor="c-title">
          Title *
        </label>
        <input
          id="c-title"
          className={inputClass}
          value={title}
          maxLength={MAX_TITLE}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Name, callsign, or designation"
        />
      </div>

      <div>
        <label className={labelClass} htmlFor="c-tagline">
          Tagline *
        </label>
        <input
          id="c-tagline"
          className={inputClass}
          value={tagline}
          maxLength={MAX_TAGLINE}
          onChange={(e) => setTagline(e.target.value)}
          placeholder="One line that sums them up"
        />
      </div>

      <div>
        <label className={labelClass} htmlFor="c-image">
          Image link
        </label>
        <input
          id="c-image"
          className={inputClass}
          value={imageUrl}
          maxLength={MAX_URL}
          onChange={(e) => setImageUrl(e.target.value)}
          placeholder="https://... (optional, shows WIP when empty)"
        />
      </div>

      <div>
        <span className={labelClass}>Links</span>
        <div className="space-y-2">
          {links.map((l, i) => (
            <div key={i} className="flex gap-2">
              <input
                className={inputClass}
                value={l}
                maxLength={MAX_URL}
                onChange={(e) => setLink(i, e.target.value)}
                placeholder="https://... (chub, discord, wherever)"
                aria-label={`Link ${i + 1}`}
              />
              <button
                type="button"
                onClick={() => setLinks((prev) => prev.filter((_, idx) => idx !== i))}
                className="shrink-0 rounded-lg border border-edge px-3 text-sm text-slate-500 transition hover:border-sos/50 hover:text-sos"
                aria-label="Remove link"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        {links.length < MAX_LINKS && (
          <button
            type="button"
            onClick={() => setLinks((prev) => [...prev, ''])}
            className="mt-2 text-xs text-signal/80 transition hover:text-signal"
          >
            + add a link ({MAX_LINKS - links.length} left)
          </button>
        )}
      </div>

      <div>
        <label className={labelClass} htmlFor="c-creator">
          Your name or handle
        </label>
        <input
          id="c-creator"
          className={inputClass}
          value={creator}
          maxLength={MAX_CREATOR}
          onChange={(e) => setCreator(e.target.value)}
          placeholder="Shown as 'by ...' on the card (optional)"
        />
      </div>

      <div>
        <label className={labelClass} htmlFor="c-desc">
          Description
        </label>
        <textarea
          id="c-desc"
          className={`${inputClass} min-h-[140px] resize-y leading-relaxed`}
          value={description}
          maxLength={MAX_DESCRIPTION}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Who they are, their story, how they talk. Markdown works. Optional."
        />
      </div>

      <div>
        <div className="mb-1.5 flex items-end justify-between">
          <label className={`${labelClass} mb-0`} htmlFor="c-lore">
            Lorebook entry
          </label>
          <span className={`text-xs tabular-nums ${tokenTone}`}>
            {tokens} / {MAX_TOKENS} tokens
          </span>
        </div>
        <textarea
          id="c-lore"
          className={`${inputClass} min-h-[220px] resize-y leading-relaxed`}
          value={lorebook}
          maxLength={MAX_CHARS}
          onChange={(e) => setLorebook(e.target.value)}
          placeholder="The compressed version bots import into their context: identity, quirks, speech style, what they want. Markdown works. Optional, capped at ~500 tokens."
        />

      </div>

      {/* quiet field: offscreen via .quiet-field, untouched by the normal flow */}
      <div className="quiet-field" aria-hidden="true">
        <label htmlFor="c-terms">I agree to the terms</label>
        <select id="c-terms" name="terms" tabIndex={-1} autoComplete="off" defaultValue="no">
          <option value="no">I do not agree</option>
          <option value="yes">I agree to the terms</option>
        </select>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-full border border-signal/50 bg-signal/10 px-5 py-3 font-display text-sm font-semibold text-signal transition hover:bg-signal/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </form>
  )
}
